import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box } from '@mui/material'
import { fetchSubtitles } from '../actions/fetchActions'
import AppBarComponent from './AppBarComponent'
import SelectionScreen from './screens/SelectionScreen'
import DashboardScreen from './screens/DashboardScreen'
import FooterComponent from './FooterComponent'
import { useAuth } from '../contexts/AuthContext'

const MapWithVariable = () => {
  const dispatch = useDispatch()
  const { keycloak } = useAuth()
  const subtitles = useSelector((state) => state.subTitles?.subtitles || [])

  // State to track the currently selected event name and the full event object
  const [selectedVariable, setSelectedVariable] = useState('')
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [selectedId, setSelectedId] = useState(null) // if the user has chosen an event
  const [selectedPhase, setSelectedPhase] = useState(null) // 'F', 'N', or 'P'

  useEffect(() => {
    if (keycloak?.token) {
      dispatch(fetchSubtitles(keycloak.token))
    }
  }, [dispatch, keycloak?.token])

  const handleSelectionChange = (e) => {
    const selectedName = e.target.value
    setSelectedVariable(selectedName)
    const matchedEvent = subtitles.find((sub) => sub.name === selectedName)
    setSelectedEvent(matchedEvent)
    setSelectedId(matchedEvent?.id || null)
    // Reset phase if user changes the event
    setSelectedPhase(null)
  }

  const handlePhaseClick = (phase) => {
    setSelectedPhase(phase)
  }

  const handleBackButtonClick = () => {
    setSelectedVariable('')
    setSelectedEvent(null)
    setSelectedId(null)
    setSelectedPhase(null)
  }

  const handleLogout = () => {
    if (keycloak?.logout) {
      keycloak.logout()
    }
  }

  // If not authenticated, show fallback
  if (!keycloak?.token) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: '#f5f5f5',
        }}
      />
    )
  }

  // Determine if user is in selection mode (no event or no phase).
  const isSelectionMode = !selectedId || !selectedPhase
  // Show back button only when user is in dashboard mode.
  const showBackButton = !isSelectionMode

  // AppBar Title and Subtitle
  const mainTitle = 'Damage Assessment'
  const appBarSubtitle = isSelectionMode ? '' : selectedEvent?.description || ''

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        // Keep overflow hidden at the root to preserve dashboard behavior
        overflow: 'hidden',
      }}
    >
      {/* AppBar always at the top */}
      <Box sx={{ height: '80px', flexShrink: 0 }}>
        <AppBarComponent
          mainTitle={mainTitle}
          subtitle={appBarSubtitle}
          username={keycloak?.tokenParsed?.preferred_username}
          onLogout={handleLogout}
          onBack={handleBackButtonClick}
          phase={selectedPhase}
          showBackButton={showBackButton}
        />
      </Box>

      {/* Main content area: conditionally scrollable in selection mode */}
      <Box
        sx={{
          height: 'calc(100vh - 80px - 64px)',
          flexShrink: 0,

          // Enable scrolling only for SelectionScreen to avoid disturbing Dashboard
          overflowY: isSelectionMode ? 'auto' : 'hidden',
          overflowX: 'hidden',
        }}
      >
        {isSelectionMode ? (
          <SelectionScreen
            value={selectedVariable}
            onChange={handleSelectionChange}
            subtitles={subtitles}
            selectedEvent={selectedEvent}
            onPhaseClick={handlePhaseClick}
          />
        ) : (
          <DashboardScreen
            selectedId={selectedId}
            phase={selectedPhase}
            token={keycloak?.token}
          />
        )}
      </Box>

      {/* Footer pinned at the bottom */}
      <Box sx={{ height: '64px', flexShrink: 0 }}>
        <FooterComponent />
      </Box>
    </Box>
  )
}

export default MapWithVariable
